import React, { useState } from 'react';
import { 
    User, 
    Database, 
    FileText, 
    Mic, 
    MessageSquare, 
    Calendar, 
    Shield,
    FileCheck, 
    BrainCircuit, 
    MessagesSquare,
    BarChart3,
    Mail,
    MoreVertical,
    ArrowLeft,
    Settings  // Added this import
  } from 'lucide-react';

const DemoCard = () => {
  const [activeTab, setActiveTab] = useState('interview');
  return (
    <div className="w-[400px] bg-white rounded-2xl shadow-lg p-6">
      <div className="flex gap-2 mb-6">
        <button 
          className={`flex-1 p-4 rounded-lg text-left ${activeTab === 'interview' ? 'bg-violet-50 border-violet-200' : 'bg-gray-50'} border transition-all`}
          onClick={() => setActiveTab('interview')}
        >
          <div className="font-medium mb-1">Audio Interview</div>
          <div className="text-sm text-gray-600">Questions with voice responses</div>
        </button>
        <button 
          className={`flex-1 p-4 rounded-lg text-left ${activeTab === 'test' ? 'bg-violet-50 border-violet-200' : 'bg-gray-50'} border transition-all`}
          onClick={() => setActiveTab('test')}
        >
          <div className="font-medium mb-1">Test Questions</div>
          <div className="text-sm text-gray-600">Multiple choice assessment</div>
        </button>
      </div>
      <div className="space-y-4">
        <input className="w-full p-3 border rounded-lg" placeholder="Position or Role" />
        <textarea 
          className="w-full p-3 border rounded-lg h-32" 
          placeholder="Enter job requirements and skills needed..."
        />
        <div className="text-sm text-gray-500">Remaining characters: 2992</div>
        <button className="bg-violet-600 text-white px-6 py-2 rounded-lg w-full hover:bg-violet-700 transition-colors">
          Generate Questions
        </button>
      </div>
    </div>
  );
};

const MetricsCard = () => (
  <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-[800px] mx-auto mt-12">
    <div className="grid grid-cols-4 gap-6 mb-8">
      <div className="text-center">
        <div className="text-2xl font-semibold text-violet-600">46,108</div>
        <div className="text-sm text-gray-600">Total Interviews</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-semibold text-green-600">42,427</div>
        <div className="text-sm text-gray-600">Assessments</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-semibold text-blue-600">40,979</div>
        <div className="text-sm text-gray-600">Reports Generated</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-semibold text-orange-600">474</div>
        <div className="text-sm text-gray-600">Active Companies</div>
      </div>
    </div>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span>Interview Completion Rate</span>
        <div className="flex items-center gap-2">
          <User size={16} />
          <span>89%</span>
        </div>
      </div>
      <div className="h-2 bg-violet-100 rounded-full">
        <div className="h-full w-[89%] bg-violet-600 rounded-full"></div>
      </div>
      <div className="flex items-center justify-between">
        <span>Assessment Success Rate</span>
        <div className="flex items-center gap-2">
          <User size={16} />
          <span>76%</span>
        </div>
      </div>
      <div className="h-2 bg-green-100 rounded-full">
        <div className="h-full w-[76%] bg-green-600 rounded-full"></div>
      </div>
    </div>
  </div>
);

const CandidateReport = () => (
  <div className="bg-violet-100 rounded-3xl p-6 max-w-md">
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b flex justify-between items-center">
        <div className="flex items-center gap-2">
          <button className="p-1 hover:bg-gray-100 rounded">
            <ArrowLeft size={20} />
          </button>
          <span>Your Personality report is ready.</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-1 hover:bg-gray-100 rounded">
            <Mail size={20} />
          </button>
          <button className="p-1 hover:bg-gray-100 rounded">
            <MoreVertical size={20} />
          </button>
        </div>
      </div>
      <div className="relative">
        <img src="/images/canditate1.png" alt="Candidate" className="w-full h-48 object-cover" />
        <div className="absolute bottom-4 right-4 bg-white p-4 rounded-xl shadow-lg max-w-[60%]">
          <p className="text-sm">
            You are intellectually curious and have a real love for learning. Your desire to continue growing and developing new skills will help you go far in your career.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const FeatureCard = ({ Icon, title, description, color }) => {
  const bgColors = {
    violet: 'bg-violet-100',
    blue: 'bg-blue-100',
    green: 'bg-green-100',
    orange: 'bg-orange-100',
    pink: 'bg-pink-100',
    indigo: 'bg-indigo-100'
  };

  const iconColors = {
    violet: 'text-violet-600',
    blue: 'text-blue-600',
    green: 'text-green-600',
    orange: 'text-orange-600',
    pink: 'text-pink-600',
    indigo: 'text-indigo-600'
  };

  return (
    <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
      <div className={`w-12 h-12 rounded-full ${bgColors[color]} flex items-center justify-center mb-6`}>
        <Icon className={`w-6 h-6 ${iconColors[color]}`} />
      </div>
      <h3 className="text-xl font-semibold mb-4">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const Testimonial = () => (
  <div className="bg-pink-100 rounded-3xl p-16 relative">
    <div className="absolute top-8 right-8">
      <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center">
        <MessageSquare className="w-6 h-6 text-white" />
      </div>
    </div>
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 leading-tight">
        "We can't go back to life before ResumeEngine.<br />
        We used to spend half of our day reading resumes!"
      </h2>
      <div className="space-y-4">
        <p className="font-medium text-lg">John Smith, Head of Talent Acquisition, Fortune 500 Company</p>
        <img src="/images/energyasaY-600x105-1.png" alt="Company Logo" className="h-16 mx-auto" />
      </div>
    </div>
  </div>
);

const IntegrationsSection = () => (
    <div className="max-w-7xl mx-auto py-20 px-4">
    <div className="mb-16 text-center">  {/* Added text-center here */}
      <h2 className="text-6xl font-bold mb-6 text-center">  {/* Added text-center here */}
        <span className="font-black">Seamless</span> integrations into your existing tools
      </h2>
      <p className="text-xl text-gray-600 max-w-4xl mx-auto text-center">  {/* Added text-center and mx-auto here */}
        Connect ResumeEngine to your ATS, calendar tools, and favorite apps to share interview insights,
        support your existing workflow, and more. We work to make our AI recruiting tool as
        accessible as possible for a wide range of platforms.
      </p>
    </div>
  
      <div className="grid md:grid-cols-3 gap-16">
        {/* ATS Integration */}
        <div className="text-center">  {/* Added text-center here */}
          <h3 className="text-2xl font-bold mb-4">Applicant Tracking Systems</h3>
          <p className="text-gray-600 mb-8">
            Leverage ResumeEngine within your ATS with bi-directional integration. We automatically 
            join interviews, share insights with your ATS, and support your existing workflow.
          </p>
          <div className="flex gap-4 justify-center">  {/* Added justify-center here */}
            <img 
              src="/images/Favicon1_iCIMS.png" 
              alt="iCIMS" 
              className="h-12 w-12 rounded-lg shadow-sm"
            />
            <img 
              src="/images/Favicon2_iCIMS.png" 
              alt="Workday" 
              className="h-12 w-12 rounded-lg shadow-sm"
            />
            <img 
              src="/images/Favicon3_iCIMS.png" 
              alt="Greenhouse" 
              className="h-12 w-12 rounded-lg shadow-sm"
            />
          </div>
        </div>
  
        {/* Video Integration */}
        <div className="text-center">  {/* Added text-center here */}
          <h3 className="text-2xl font-bold mb-4">Audio & Transcription</h3>
          <p className="text-gray-600 mb-8">
            ResumeEngine records, transcribes, and automatically creates highlights of 
            every audio interview. Interviewers can review and analyze interviews with 
            our advanced AI analysis tools.
          </p>
          <div className="flex gap-4 justify-center">  {/* Added justify-center here */}
            <div className="h-12 w-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <Mic className="h-6 w-6 text-white" />
            </div>
            <div className="h-12 w-12 bg-purple-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="h-6 w-6 text-white" />
            </div>
            <div className="h-12 w-12 bg-green-500 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
  
        {/* Calendar Integration */}
        <div className="text-center">  {/* Added text-center here */}
          <h3 className="text-2xl font-bold mb-4">Calendar</h3>
          <p className="text-gray-600 mb-8">
            No need for double calendar invites here. Connect your calendar so your
            interview invites automatically sync to ResumeEngine.
          </p>
          <div className="flex gap-4 justify-center">  {/* Added justify-center here */}
            <div className="h-12 w-12 bg-[#2684FC] rounded-lg flex items-center justify-center">
            <img 
                src="/images/Favicon5_iCIMS.png" 
                alt="Outlook" 
                className="h-6 w-6"
              />
            </div>
            <div className="h-12 w-12 bg-[#0078D4] rounded-lg flex items-center justify-center">
              <img 
                src="/images/Favicon4_iCIMS.png" 
                alt="Outlook" 
                className="h-6 w-6"
              />
            </div>
            <div className="h-12 w-12 bg-[#0098FF] rounded-lg flex items-center justify-center">
              <Calendar className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
);

const AgentCard = ({ name, type, description, features, color, hasFreePlan = false, imageUrl }) => (
    <div className="bg-white rounded-2xl p-8 shadow-lg relative">
      {/* Image container with overflow for the top part */}
      <div className={`h-56 rounded-xl mb-6 relative overflow-visible ${color}`}>
        <div className="absolute -top-8 left-0 right-0 h-64 flex justify-center">
          <img 
            src={imageUrl} 
            alt={name} 
            className="h-full w-auto object-contain"
          />
        </div>
        
      </div>
      
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-bold">{name}</h3>
          <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm font-medium">
            {type}
          </span>
        </div>
        
        <p className="text-gray-600">{description}</p>
        
        <div className="space-y-2">
          <h4 className="font-semibold">Features</h4>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-3 h-3 text-violet-600" />
                </div>
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="space-y-3 pt-4">
          <button className="w-full bg-violet-600 text-white rounded-lg py-3 hover:bg-violet-700 transition-colors">
            {`Learn more about ${name}`}
          </button>
          <button className="w-full border-2 border-violet-600 text-violet-600 rounded-lg py-3 hover:bg-violet-50 transition-colors">
            {hasFreePlan ? 'Request a demo' : 'Request a demo'}
          </button>
        </div>
      </div>
    </div>
  );

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50">
      {/* Navigation */}
      <nav className="flex justify-between items-center px-8 py-4 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="text-2xl font-bold">ResumeEngine</div>
        <div className="flex items-center gap-8">
          <a href="#about" className="hover:text-violet-600 transition-colors">About Us</a>
          <a href="#blog" className="hover:text-violet-600 transition-colors">Blog</a>
          <a href="#contact" className="hover:text-violet-600 transition-colors">Contact</a>
          <div className="h-4 w-px bg-gray-200"></div>
          <button className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors">
            Book A Demo
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="text-center px-4 py-12">
      <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-violet-900 to-blue-900 inline-block text-transparent bg-clip-text">
            Hire Top Talent with AI
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Streamline your hiring process with ResumeEngine the AI-powered
            candidate assessment platform with audio interviews and intelligent analysis
        </p>
        <button className="bg-violet-600 text-white px-8 py-4 rounded-xl mb-4 hover:bg-violet-700 transition-colors">
            Request Demo
        </button>
        <p className="text-gray-500">Instant Setup and Activation</p>
        </div>

        {/* Main Section */}
        <div className="max-w-7xl mx-auto py-20 px-4">
        <div className="flex items-center justify-between gap-20">
            <div className="flex-1 space-y-6">
            <h2 className="text-5xl font-bold leading-tight">
                Find the people who<br />
                belong with your<br />
                brand
            </h2>
            
            <div className="space-y-6 text-xl text-gray-600">
                <p>
                ResumeEngine's automated chat lets you interview everyone. And
                because it's low pressure, every candidate will bring you their
                real and best self.
                </p>
                
                <p>
                The result? You see the whole person, not just their CV.
                </p>
            </div>

            
            </div>

            <div className="flex-1">
            <div className="bg-violet-100 rounded-3xl p-6">
                <div className="relative">
                <img 
                    src="/images/agent1.png" 
                    alt="Candidate Chat Interface" 
                    className="rounded-xl"
                />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl p-4 max-w-xs shadow-lg">
                    <div className="space-y-4">
                    <div className="space-y-4">
                        <p className="text-gray-700">Hi! I'm your AI interviewer.</p>
                        <p className="text-gray-700">
                        Let's start with a quick introduction. Could you tell me a bit about yourself?
                        </p>
                    </div>
                    <div className="bg-black text-white p-3 rounded-xl text-sm">
                        Hi, My name is John and I'm a software engineer..
                    </div>
                    </div>
                </div>
                </div>
            </div>
            </div>
        </div>
        </div>

        {/* Interview Setup Section */}
        <div className="max-w-7xl mx-auto py-8 px-4">  {/* Changed py-16 to py-8 */}
        <h2 className="text-4xl font-bold mb-16 text-center">
            Start interviewing in 5 minutes
        </h2>
        <div className="bg-white/50 backdrop-blur-sm rounded-3xl p-12 flex gap-12 items-center shadow-xl">
          <div className="flex-1">
            <p className="text-lg mb-8 text-gray-700 leading-relaxed">
              ResumeEngine starts shortening your processes from question sets.
              It's possible to start candidate interviews within 5 minutes.
            </p>
            <ul className="space-y-6">
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm">✓</div>
                <span>Pre-made question sets used worldwide</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm">✓</div>
                <span>Intelligent question sets created with artificial intelligence</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm">✓</div>
                <span>Custom manual question sets tailored for your company</span>
              </li>
            </ul>
          </div>
          <DemoCard />
        </div>
      </div>

      {/* Candidate Experience Section */}
      <div className="max-w-7xl mx-auto py-20 px-4">
        <div className="flex flex-wrap items-center gap-20">
          <div className="flex-[0.9] min-w-[400px]">
            <h2 className="text-4xl font-bold mb-12">
              Now you can treat your candidates like you treat your customers.
            </h2>
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-4 h-4 text-violet-600" />
                </div>
                <p className="text-lg text-gray-700">
                  Meet candidates where they are with our asynchronous audio interviews and AI-powered assessment.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-4 h-4 text-violet-600" />
                </div>
                <p className="text-lg text-gray-700">
                  Get the best from your candidates from an environment that's untimed and stress free.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-4 h-4 text-violet-600" />
                </div>
                <p className="text-lg text-gray-700">
                  Make every candidate feel seen with personalised coaching insights post interview.
                </p>
              </div>
              <button className="inline-flex items-center text-lg font-medium text-violet-600 hover:text-violet-700 border-2 border-violet-600 rounded-full px-6 py-2 transition-colors">
                Talk To Our Team
              </button>
            </div>
          </div>
          <div className="flex-[1.1] min-w-[500px]">
            <div className="bg-violet-100 rounded-3xl p-8">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-6 border-b flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <button className="p-2 hover:bg-gray-100 rounded">
                      <ArrowLeft size={24} />
                    </button>
                    <span className="text-lg">Your Personality report is ready.</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button className="p-2 hover:bg-gray-100 rounded">
                      <Mail size={24} />
                    </button>
                    <button className="p-2 hover:bg-gray-100 rounded">
                      <MoreVertical size={24} />
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <img src="/images/canditate1.png" alt="Candidate" className="w-full h-72 object-cover" />
                  <div className="absolute bottom-6 right-6 bg-white p-6 rounded-xl shadow-xl max-w-[70%]">
                    <p className="text-base leading-relaxed">
                      You are intellectually curious and have a real love for learning. Your desire to continue growing and developing new skills will help you go far in your career.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comprehensive Assessment Section */}
      <div className="max-w-7xl mx-auto py-20 px-4 text-center">
        <h2 className="text-4xl font-bold mb-6">
          A single interview. The whole candidate.
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-16">
          In a ResumeEngine interview, every candidate answers carefully crafted questions that reveal their true self. 
          Our AI automatically ranks every candidate according to their fit with your brand, based on three criteria:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-violet-50 p-8 rounded-2xl">
          <div className="w-16 h-16 bg-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <User className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Personality traits</h3>
          </div>
          <div className="bg-black p-8 rounded-2xl">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6">
              <BrainCircuit className="w-8 h-8 text-black" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-white">Behavioural competencies</h3>
          </div>
          <div className="bg-pink-50 p-8 rounded-2xl">
            <div className="w-16 h-16 bg-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <MessagesSquare className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Communication skills</h3>
          </div>
        </div>
        <p className="text-lg font-medium mt-12">
          Result? You slash the time it takes to hire the right people for your brand.
        </p>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto py-16 px-4">
        <h2 className="text-4xl font-bold text-center mb-4">
          Features that shorten your processes
        </h2>
        <p className="text-center text-gray-600 mb-16">
          We're your biggest ally in identifying the most suitable candidate.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            Icon={Mic}
            color="violet"
            title="Audio Interview"
            description="Conduct real-time audio interviews with AI analysis and instant transcription."
          />
          <FeatureCard
            Icon={MessageSquare}
            color="blue"
            title="Interview Reports"
            description="Get detailed transcripts and AI-powered insights from each interview."
          />
          <FeatureCard
            Icon={FileText}
            color="green"
            title="CV Analysis"
            description="Convert resumes into structured data with advanced parsing technology."
          />
          <FeatureCard
            Icon={Database}
            color="orange"
            title="Candidate Database"
            description="Query millions of CVs using natural language to find perfect matches."
          />
          <FeatureCard
            Icon={Calendar}
            color="pink"
            title="Ease of Use"
            description="Start interviews in minutes with our intuitive interface and ready-to-use templates."
          />
          <FeatureCard
            Icon={Shield}
            color="indigo"
            title="Security First"
            description="Enterprise-grade security and compliance for all your recruitment data."
          />
        </div>
      </div>

      {/* Metrics Dashboard */}
      <MetricsCard />

      {/* Testimonial Section */}
      {/* <div className="max-w-7xl mx-auto py-20 px-4">
        <Testimonial />
      </div> */}

      {/* Integrations Section */}
        <IntegrationsSection />

    {/* Fair Section */}
    <div className="max-w-7xl mx-auto py-20 px-4">
        <div className="flex items-center justify-between gap-20">
            <div className="flex-1">
            <h2 className="text-5xl font-bold mb-12">
                Get fair  hiring, fast
            </h2>
            
            <div className="space-y-8">
                <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <p className="text-xl text-gray-800">
                    See an increase in diversity hires within weeks by casting a wider net.
                </p>
                </div>

                <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <p className="text-xl text-gray-800">
                    Create an equitable hiring experience with an unbiased AI assessment that values every candidate.
                </p>
                </div>

                <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <p className="text-xl text-gray-800">
                    Make confident decisions while we handle the candidate shortlist for you.
                </p>
                </div>
            </div>

            <button className="mt-12 px-8 py-3 border-2 border-black rounded-full text-lg font-medium hover:bg-gray-50 transition-colors">
                Talk To Our Team
            </button>
            </div>

            <div className="flex-1">
            <div className="bg-violet-100 rounded-3xl p-8">
                <div className="bg-white rounded-2xl p-6 space-y-6">
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                    <span className="text-gray-600">Applied</span>
                    <div className="w-64 h-6 bg-violet-100 rounded-full overflow-hidden">
                        <div className="w-3/4 h-full bg-violet-300"></div>
                    </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                    <span className="text-gray-600">Recommended</span>
                    <div className="w-64 h-6 bg-violet-100 rounded-full overflow-hidden">
                        <div className="w-1/2 h-full bg-violet-300"></div>
                    </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                    <span className="text-gray-600">Hired</span>
                    <div className="w-64 h-6 bg-violet-100 rounded-full overflow-hidden">
                        <div className="w-1/3 h-full bg-violet-300"></div>
                    </div>
                    </div>
                </div>

                <div className="pt-6 border-t">
                    <div className="flex items-center gap-8">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span>Male</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-violet-300"></div>
                        <span>Female</span>
                    </div>
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                    0 &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 25% &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 50%
                    </div>
                </div>
                </div>
            </div>
            </div>
        </div>
        </div>

      {/* AI Agents Section */}
    <div className="max-w-7xl mx-auto py-20 px-4">
    <div className="grid md:grid-cols-3 gap-8">
        <AgentCard
        name="Interview"
        type="SCREENING"
        description="Conducts initial screening interviews and evaluates candidate responses"
        color="bg-sky-200"
        hasFreePlan={true}
        imageUrl="/images/agent5.png"
        features={[
            "AI-powered audio interview assessment",
            "Real-time response analysis",
            "Automated candidate scoring",
            "Custom interview templates"
        ]}
        />
        <AgentCard
        name="Assessment"
        type="EVALUATION"
        description="Deep analysis of candidate skills and behavioral patterns"
        color="bg-green-200"
        imageUrl="/images/agent3.png"
        features={[
            "In-depth personality assessment",
            "Skills and competency mapping",
            "Cultural fit analysis",
            "Behavioral pattern recognition"
        ]}
        />
        <AgentCard
        name="Talent"
        type="MATCHING"
        description="Advanced candidate-role matching and recommendation engine"
        color="bg-amber-200"
        imageUrl="/images/agent2.png"
        features={[
            "Intelligent candidate ranking",
            "Job requirement matching",
            "Skill gap analysis",
            "Team fit predictions"
        ]}
        />
    </div>

    <div className="mt-20 bg-violet-600 rounded-3xl p-6 flex items-center justify-between">
        <div className="space-y-4">
        <h2 className="text-3xl font-bold text-white">Custom AI Solution</h2>
        <p className="text-violet-100 max-w-2xl">
            Our AI recruitment solutions can be customized to your specific hiring needs 
            and integrate seamlessly with your existing HR tools. Enterprise-grade security 
            and GDPR compliance.
        </p>
        <button className="bg-white text-violet-600 px-6 py-3 rounded-lg hover:bg-violet-50 transition-colors">
            Request a demo
        </button>
        </div>
        <div className="bg-violet-500 rounded-2xl p-4 relative">
        <img 
            src="/images/agent4.png"
            alt="AI Agent Assistant"
            className="w-72 h-auto"
        />
        </div>
    </div>
    </div>

    {/* Final CTA */}
      <div className="text-center py-20 bg-gradient-to-br from-violet-50 via-blue-50 to-purple-50">
        <h2 className="text-4xl font-bold mb-6">
          Meet the future of recruitment<br />technologies today!
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
          Managing the recruitment process with our user-friendly interface is now
          easier than ever. Discover a reliable way to accelerate your hiring and find
          the best talents today!
        </p>
        <button className="bg-violet-600 text-white px-8 py-4 rounded-xl hover:bg-violet-700 transition-colors">
          Contact Us
        </button>
      </div>

      {/* Footer */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold">ResumeEngine</div>
            <div className="flex gap-8">
              <a href="#blog" className="hover:text-violet-600 transition-colors">Blog</a>
              <a href="#contact" className="hover:text-violet-600 transition-colors">Contact</a>
            </div>
          </div>
          <div className="flex justify-between text-sm text-gray-500 mt-8 pt-8 border-t">
            <div>Copyright © 2024</div>
            <div className="flex gap-8">
              <a href="#privacy" className="hover:text-gray-700 transition-colors">Privacy Policy</a>
              <a href="#cookies" className="hover:text-gray-700 transition-colors">Cookie Policy</a>
              <a href="#protection" className="hover:text-gray-700 transition-colors">Personal Data Protection</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}